import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, PaperClipIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { campaignApi, whatsappApi } from '../../api';

const STATUSES = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'];

const extractPlaceholderIndexes = (text) => {
  if (!text || typeof text !== 'string') return [];
  const indexes = new Set();
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let match = regex.exec(text);
  while (match) {
    indexes.add(Number(match[1]));
    match = regex.exec(text);
  }
  return Array.from(indexes).sort((a, b) => a - b);
};

const buildTemplateRequirements = (template) => {
  if (!template || !Array.isArray(template.components)) {
    return {
      headerFormat: '',
      headerMediaType: null,
      headerTextPlaceholders: [],
      bodyTextPlaceholders: [],
    };
  }

  const header = template.components.find((component) => String(component?.type || '').toUpperCase() === 'HEADER');
  const body = template.components.find((component) => String(component?.type || '').toUpperCase() === 'BODY');

  const headerFormat = String(header?.format || '').toUpperCase();
  const headerMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)
    ? headerFormat.toLowerCase()
    : null;

  const headerText = headerFormat === 'TEXT' ? String(header?.text || '') : '';
  const bodyText = String(body?.text || '');

  return {
    headerFormat,
    headerMediaType,
    headerTextPlaceholders: extractPlaceholderIndexes(headerText),
    bodyTextPlaceholders: extractPlaceholderIndexes(bodyText),
  };
};

const buildWhatsappTemplateComponents = ({
  requirements,
  variables,
  attachmentUrl,
}) => {
  const components = [];

  if (requirements.headerMediaType) {
    if (attachmentUrl) {
      components.push({
        type: 'header',
        parameters: [{
          type: requirements.headerMediaType,
          [requirements.headerMediaType]: { link: attachmentUrl },
        }],
      });
    }
  } else if (requirements.headerTextPlaceholders.length > 0) {
    components.push({
      type: 'header',
      parameters: requirements.headerTextPlaceholders.map((index) => ({
        type: 'text',
        text: String(variables.header[index] || ''),
      })),
    });
  }

  if (requirements.bodyTextPlaceholders.length > 0) {
    components.push({
      type: 'body',
      parameters: requirements.bodyTextPlaceholders.map((index) => ({
        type: 'text',
        text: String(variables.body[index] || ''),
      })),
    });
  }

  return components;
};

const getInitialFormData = ({ campaign, audiences }) => {
  if (campaign) {
    return {
      name: campaign.name || '',
      description: campaign.description || '',
      audienceId: campaign.audienceId || '',
      channel: campaign.channel || 'EMAIL',
      status: campaign.status || 'DRAFT',
      startTime: campaign.startTime ? new Date(campaign.startTime).toISOString().slice(0, 16) : '',
      content: {
        subject: campaign.content?.subject || '',
        message: campaign.content?.message || '',
        attachmentUrl: campaign.content?.attachmentUrl || '',
        attachmentName: campaign.content?.attachmentName || '',
        whatsappBusinessAccountId: campaign.content?.whatsappBusinessAccountId || '',
        whatsappPhoneNumberId: campaign.content?.whatsappPhoneNumberId || '',
        whatsappTemplateId: campaign.content?.whatsappTemplateId || '',
        whatsappTemplateName: campaign.content?.whatsappTemplateName || '',
        whatsappTemplateLanguage: campaign.content?.whatsappTemplateLanguage || '',
        whatsappTemplateComponents: Array.isArray(campaign.content?.whatsappTemplateComponents)
          ? campaign.content.whatsappTemplateComponents
          : [],
      },
    };
  }

  return {
    name: '',
    description: '',
    audienceId: audiences.length > 0 ? audiences[0].id : '',
    channel: audiences.length > 0 ? audiences[0].channel : 'EMAIL',
    status: 'DRAFT',
    startTime: '',
    content: {
      subject: '',
      message: '',
      attachmentUrl: '',
      attachmentName: '',
      whatsappBusinessAccountId: '',
      whatsappPhoneNumberId: '',
      whatsappTemplateId: '',
      whatsappTemplateName: '',
      whatsappTemplateLanguage: '',
      whatsappTemplateComponents: [],
    },
  };
};

const CampaignForm = ({ show, campaign, audiences, onSubmit, onClose }) => {
  const [formData, setFormData] = useState(getInitialFormData({ campaign, audiences }));
  const [uploading, setUploading] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formError, setFormError] = useState('');

  const [waBusinessAccounts, setWaBusinessAccounts] = useState([]);
  const [waPhoneNumbers, setWaPhoneNumbers] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [loadingWaAccounts, setLoadingWaAccounts] = useState(false);
  const [loadingWaPhones, setLoadingWaPhones] = useState(false);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);

  const [templateRequirements, setTemplateRequirements] = useState({
    headerFormat: '',
    headerMediaType: null,
    headerTextPlaceholders: [],
    bodyTextPlaceholders: [],
  });
  const [templateVariables, setTemplateVariables] = useState({ header: {}, body: {} });

  const selectedAudience = useMemo(
    () => audiences.find((audience) => audience.id === formData.audienceId),
    [audiences, formData.audienceId]
  );

  const selectedChannel = selectedAudience?.channel || formData.channel;
  const isWhatsappCampaign = selectedChannel === 'WHATSAPP';

  useEffect(() => {
    if (!show) return;

    setFormData(getInitialFormData({ campaign, audiences }));
    setFormError('');
    setTestEmails('');
    setTestResult(null);

    const existingComponents = Array.isArray(campaign?.content?.whatsappTemplateComponents)
      ? campaign.content.whatsappTemplateComponents
      : [];

    const headerComponent = existingComponents.find((component) => String(component?.type || '').toLowerCase() === 'header');
    const bodyComponent = existingComponents.find((component) => String(component?.type || '').toLowerCase() === 'body');

    const headerVariables = {};
    const bodyVariables = {};

    (headerComponent?.parameters || []).forEach((parameter, index) => {
      if (parameter?.type === 'text') {
        headerVariables[index + 1] = parameter.text || '';
      }
    });

    (bodyComponent?.parameters || []).forEach((parameter, index) => {
      if (parameter?.type === 'text') {
        bodyVariables[index + 1] = parameter.text || '';
      }
    });

    setTemplateVariables({ header: headerVariables, body: bodyVariables });
  }, [show, campaign, audiences]);

  useEffect(() => {
    if (!show || !isWhatsappCampaign) {
      return;
    }

    let mounted = true;
    const loadAccounts = async () => {
      setLoadingWaAccounts(true);
      try {
        const accounts = await whatsappApi.listBusinessAccounts();
        if (!mounted) return;
        const list = Array.isArray(accounts) ? accounts : [];
        setWaBusinessAccounts(list);

        const currentId = formData.content.whatsappBusinessAccountId;
        if (!currentId && list.length > 0) {
          setFormData((prev) => ({
            ...prev,
            content: {
              ...prev.content,
              whatsappBusinessAccountId: list[0].id,
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load WhatsApp business accounts', error);
        if (mounted) {
          setWaBusinessAccounts([]);
        }
      } finally {
        if (mounted) {
          setLoadingWaAccounts(false);
        }
      }
    };

    loadAccounts();

    return () => {
      mounted = false;
    };
  }, [show, isWhatsappCampaign]);

  useEffect(() => {
    if (!show || !isWhatsappCampaign) {
      return;
    }

    const businessAccountId = formData.content.whatsappBusinessAccountId;
    if (!businessAccountId) {
      setWaPhoneNumbers([]);
      setWaTemplates([]);
      return;
    }

    let mounted = true;

    const loadPhoneNumbersAndTemplates = async () => {
      setLoadingWaPhones(true);
      setLoadingWaTemplates(true);

      try {
        const [phoneNumbersData, templatesData] = await Promise.all([
          whatsappApi.listSavedPhoneNumbers(businessAccountId),
          whatsappApi.listSavedTemplates(businessAccountId),
        ]);

        if (!mounted) return;

        const phones = Array.isArray(phoneNumbersData) ? phoneNumbersData : [];
        const templates = Array.isArray(templatesData) ? templatesData : [];

        setWaPhoneNumbers(phones);
        setWaTemplates(templates);

        setFormData((prev) => {
          const nextContent = { ...prev.content };

          if (!phones.some((phone) => phone.id === nextContent.whatsappPhoneNumberId)) {
            nextContent.whatsappPhoneNumberId = phones[0]?.id || '';
          }

          const selectedTemplate = templates.find((template) => (
            template.id === nextContent.whatsappTemplateId || (
              template.name === nextContent.whatsappTemplateName &&
              template.language === nextContent.whatsappTemplateLanguage
            )
          ));

          if (selectedTemplate) {
            nextContent.whatsappTemplateId = selectedTemplate.id || '';
            nextContent.whatsappTemplateName = selectedTemplate.name || '';
            nextContent.whatsappTemplateLanguage = selectedTemplate.language || '';
            setTemplateRequirements(buildTemplateRequirements(selectedTemplate));
          } else if (templates.length > 0) {
            nextContent.whatsappTemplateId = templates[0].id || '';
            nextContent.whatsappTemplateName = templates[0].name || '';
            nextContent.whatsappTemplateLanguage = templates[0].language || '';
            setTemplateRequirements(buildTemplateRequirements(templates[0]));
            setTemplateVariables({ header: {}, body: {} });
          } else {
            nextContent.whatsappTemplateId = '';
            nextContent.whatsappTemplateName = '';
            nextContent.whatsappTemplateLanguage = '';
            setTemplateRequirements({
              headerFormat: '',
              headerMediaType: null,
              headerTextPlaceholders: [],
              bodyTextPlaceholders: [],
            });
            setTemplateVariables({ header: {}, body: {} });
          }

          return {
            ...prev,
            content: nextContent,
          };
        });
      } catch (error) {
        console.error('Failed to load WhatsApp phone numbers/templates', error);
        if (mounted) {
          setWaPhoneNumbers([]);
          setWaTemplates([]);
        }
      } finally {
        if (mounted) {
          setLoadingWaPhones(false);
          setLoadingWaTemplates(false);
        }
      }
    };

    loadPhoneNumbersAndTemplates();

    return () => {
      mounted = false;
    };
  }, [show, isWhatsappCampaign, formData.content.whatsappBusinessAccountId]);

  const handleAudienceChange = (audienceId) => {
    const audience = audiences.find((item) => item.id === audienceId);
    const nextChannel = audience?.channel || formData.channel;

    setFormData((prev) => ({
      ...prev,
      audienceId,
      channel: nextChannel,
    }));
    setFormError('');
  };

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setFormError('');
    try {
      const url = await campaignApi.uploadAttachment(file, file.name);
      setFormData((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          attachmentUrl: url,
          attachmentName: file.name,
        },
      }));
    } catch (error) {
      console.error('Error uploading attachment:', error);
      setFormError('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = () => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        attachmentUrl: '',
        attachmentName: '',
      },
    }));
  };

  const handleTemplateSelect = (templateId) => {
    const selectedTemplate = waTemplates.find((template) => template.id === templateId) || null;

    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        whatsappTemplateId: selectedTemplate?.id || '',
        whatsappTemplateName: selectedTemplate?.name || '',
        whatsappTemplateLanguage: selectedTemplate?.language || '',
      },
    }));

    setTemplateRequirements(buildTemplateRequirements(selectedTemplate));
    setTemplateVariables({ header: {}, body: {} });
    setFormError('');
  };

  const handleTemplateVariableChange = (scope, index, value) => {
    setTemplateVariables((prev) => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        [index]: value,
      },
    }));
  };

  const validateWhatsappForm = () => {
    const content = formData.content;

    if (!content.whatsappBusinessAccountId) {
      return 'Missing whatsappBusinessAccountId';
    }

    if (!content.whatsappPhoneNumberId) {
      return 'Missing whatsappPhoneNumberId';
    }

    if (!content.whatsappTemplateName) {
      return 'Missing whatsappTemplateName';
    }

    if (!content.whatsappTemplateLanguage) {
      return 'Missing whatsappTemplateLanguage';
    }

    if (!waPhoneNumbers.some((phone) => phone.id === content.whatsappPhoneNumberId)) {
      return 'Selected phone number not in selected business account';
    }

    const templateMatch = waTemplates.find((template) => (
      (content.whatsappTemplateId && template.id === content.whatsappTemplateId) ||
      (template.name === content.whatsappTemplateName && template.language === content.whatsappTemplateLanguage)
    ));

    if (!templateMatch) {
      return 'Selected template not available in selected business account';
    }

    if (content.whatsappTemplateId && templateMatch.id !== content.whatsappTemplateId) {
      return 'whatsappTemplateId does not match selected template name/language';
    }

    if (templateRequirements.headerMediaType && !content.attachmentUrl) {
      return `Template requires a header ${templateRequirements.headerMediaType} attachment`;
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!formData.name || !formData.audienceId) {
      setFormError('Name and audience are required');
      return;
    }

    const submitData = {
      ...formData,
      channel: selectedChannel,
      startTime: formData.startTime || null,
      content: {
        ...formData.content,
      },
    };

    if (selectedChannel === 'WHATSAPP') {
      const validationError = validateWhatsappForm();
      if (validationError) {
        setFormError(validationError);
        return;
      }

      submitData.content.whatsappTemplateComponents = buildWhatsappTemplateComponents({
        requirements: templateRequirements,
        variables: templateVariables,
        attachmentUrl: formData.content.attachmentUrl,
      });

      submitData.content.subject = '';
      submitData.content.message = '';
    }

    if (campaign) {
      submitData.id = campaign.id;
    }

    try {
      await onSubmit(submitData);
    } catch (error) {
      setFormError(error?.message || 'Failed to save campaign');
    }
  };

  const handleSendTestEmail = async () => {
    if (!campaign?.id) {
      setFormError('Please save the campaign first before sending test emails');
      return;
    }

    const emails = testEmails
      .split(/[\n,;]+/)
      .map((email) => email.trim())
      .filter((email) => email && email.includes('@'));

    if (emails.length === 0) {
      setFormError('Please enter at least one valid email address');
      return;
    }

    setSendingTest(true);
    setFormError('');
    setTestResult(null);
    try {
      await campaignApi.sendTestEmail(campaign.id, emails);
      setTestResult({ success: true, message: `Test email sent to ${emails.length} recipient(s)` });
      setTestEmails('');
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestResult({ success: false, message: error.response?.data?.message || 'Failed to send test email' });
    } finally {
      setSendingTest(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative top-5 mx-auto mb-10 w-full max-w-4xl rounded-md border bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {campaign ? 'Edit Campaign' : 'Create Campaign'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Campaign Name *</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., WA Promo April"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Campaign description..."
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Target Audience *</label>
              <select
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                value={formData.audienceId}
                onChange={(e) => handleAudienceChange(e.target.value)}
              >
                <option value="">Select an audience</option>
                {audiences.map((audience) => (
                  <option key={audience.id} value={audience.id}>
                    {audience.name} ({audience.channel} - {audience.currentSize || 0} members)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Channel</label>
              <input
                type="text"
                readOnly
                className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm sm:text-sm"
                value={selectedChannel}
              />
              <p className="mt-1 text-xs text-gray-500">Channel is determined by the selected audience</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Schedule (Start Time)</label>
            <input
              type="datetime-local"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
              value={formData.startTime}
              onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="mb-4 text-sm font-medium text-gray-900">Campaign Content</h4>

            {isWhatsappCampaign ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Account *</label>
                    <select
                      value={formData.content.whatsappBusinessAccountId}
                      onChange={(e) => {
                        const businessAccountId = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          content: {
                            ...prev.content,
                            whatsappBusinessAccountId: businessAccountId,
                            whatsappPhoneNumberId: '',
                            whatsappTemplateId: '',
                            whatsappTemplateName: '',
                            whatsappTemplateLanguage: '',
                          },
                        }));
                      }}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    >
                      <option value="">Select business account</option>
                      {waBusinessAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name || account.id}
                        </option>
                      ))}
                    </select>
                    {loadingWaAccounts && <p className="mt-1 text-xs text-gray-500">Loading business accounts...</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                    <select
                      value={formData.content.whatsappPhoneNumberId}
                      onChange={(e) => {
                        const phoneNumberId = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          content: {
                            ...prev.content,
                            whatsappPhoneNumberId: phoneNumberId,
                          },
                        }));
                      }}
                      disabled={!formData.content.whatsappBusinessAccountId}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 disabled:cursor-not-allowed disabled:bg-gray-100 sm:text-sm"
                    >
                      <option value="">Select phone number</option>
                      {waPhoneNumbers.map((phone) => (
                        <option key={phone.id} value={phone.id}>
                          {(phone.displayPhoneNumber || phone.id)}{phone.verifiedName ? ` - ${phone.verifiedName}` : ''}
                        </option>
                      ))}
                    </select>
                    {loadingWaPhones && <p className="mt-1 text-xs text-gray-500">Loading phone numbers...</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template *</label>
                    <select
                      value={formData.content.whatsappTemplateId}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      disabled={!formData.content.whatsappBusinessAccountId}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 disabled:cursor-not-allowed disabled:bg-gray-100 sm:text-sm"
                    >
                      <option value="">Select template</option>
                      {waTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.language || 'unknown'})
                        </option>
                      ))}
                    </select>
                    {loadingWaTemplates && <p className="mt-1 text-xs text-gray-500">Loading templates...</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template Language *</label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm sm:text-sm"
                      value={formData.content.whatsappTemplateLanguage}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        content: {
                          ...prev.content,
                          whatsappTemplateLanguage: e.target.value,
                        },
                      }))}
                    />
                  </div>
                </div>

                {(templateRequirements.headerTextPlaceholders.length > 0 || templateRequirements.bodyTextPlaceholders.length > 0) && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <p className="mb-3 text-sm font-medium text-gray-700">Template Variables</p>

                    <div className="space-y-3">
                      {templateRequirements.headerTextPlaceholders.map((index) => (
                        <div key={`header-${index}`}>
                          <label className="block text-xs font-medium text-gray-700">Header Variable {index}</label>
                          <input
                            type="text"
                            value={templateVariables.header[index] || ''}
                            onChange={(e) => handleTemplateVariableChange('header', index, e.target.value)}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>
                      ))}

                      {templateRequirements.bodyTextPlaceholders.map((index) => (
                        <div key={`body-${index}`}>
                          <label className="block text-xs font-medium text-gray-700">Body Variable {index}</label>
                          <input
                            type="text"
                            value={templateVariables.body[index] || ''}
                            onChange={(e) => handleTemplateVariableChange('body', index, e.target.value)}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {templateRequirements.headerMediaType && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    This template requires a header {templateRequirements.headerMediaType}. Upload an attachment below.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject Line</label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    value={formData.content.subject}
                    onChange={(e) => setFormData((prev) => ({
                      ...prev,
                      content: { ...prev.content, subject: e.target.value },
                    }))}
                    placeholder="Email subject line..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    rows={5}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    value={formData.content.message}
                    onChange={(e) => setFormData((prev) => ({
                      ...prev,
                      content: { ...prev.content, message: e.target.value },
                    }))}
                    placeholder="Your campaign message..."
                  />
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Attachment (optional)</label>
              {formData.content.attachmentUrl ? (
                <div className="mt-1 flex items-center gap-2 rounded-md bg-gray-50 p-3">
                  <PaperClipIcon className="h-5 w-5 text-gray-400" />
                  <span className="flex-1 text-sm text-gray-700">{formData.content.attachmentName}</span>
                  <button type="button" onClick={removeAttachment} className="text-sm text-red-600 hover:text-red-800">
                    Remove
                  </button>
                </div>
              ) : (
                <div className="mt-1">
                  <input
                    type="file"
                    onChange={handleAttachmentUpload}
                    disabled={uploading}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-purple-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50"
                  />
                  {uploading && <p className="mt-1 text-sm text-gray-500">Uploading...</p>}
                </div>
              )}
            </div>
          </div>

          {campaign && selectedChannel === 'EMAIL' && (
            <div className="border-t pt-4">
              <h4 className="mb-4 text-sm font-medium text-gray-900">
                <PaperAirplaneIcon className="mr-2 inline h-4 w-4" />
                Send Test Email
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Test Email Recipients</label>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    value={testEmails}
                    onChange={(e) => setTestEmails(e.target.value)}
                    placeholder="Enter email addresses (one per line, or separated by commas)"
                  />
                </div>

                {testResult && (
                  <div className={`rounded-md p-3 ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    <p className="text-sm">{testResult.message}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmails.trim()}
                  className="flex items-center gap-2 rounded-md border border-purple-300 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendingTest ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-purple-700"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4" />
                      Send Test Email
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || loadingWaAccounts || loadingWaPhones || loadingWaTemplates}
              className="rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {campaign ? 'Update' : 'Create'} Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;
